import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Input,
  Space,
  Steps,
  Tag,
  Typography,
} from 'antd';
import {
  AuditOutlined,
  CheckCircleFilled,
  ExperimentOutlined,
  PlayCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { validateFlow, diffFlow, publishFlow } from '@/api/flows';
import { submitForApproval, decideApproval } from '@/api/approvals';
import { getSimulation, runSimulation } from '@/api/simulations';
import { normalizeApiError, isNotAvailable, type NormalizedApiError } from '@/api/errors';
import type { Approval, DiffResult, PublishResult, SimulationView, ValidationResult } from '@/api/types';
import { FanOutReport } from './FanOutReport';
import { ChangeClassBadge } from './DiffPanel';

const { Text, Paragraph } = Typography;

type StepError = NormalizedApiError & { action: string };

export interface PublishDrawerProps {
  open: boolean;
  flowId: string;
  version: number;
  publishedVersion: number | null;
  /** The canonical IR source (JSON) of the version being published — for the validate pass. */
  source: string;
  currentUserSub?: string;
  onClose: () => void;
  onPublished: (result: PublishResult) => void;
  /**
   * OPSFL-FLOWOPS-1 A1: after a successful publish the pipeline's terminal step becomes
   * ACTIONABLE — "Run it now" hands the published (flowId, version) to the caller, which opens the
   * Start-run drawer. Optional so the drawer keeps working where no start affordance is mounted.
   */
  onRunItNow?: (flowId: string, version: number) => void;
}

/**
 * The publish gate (spec §11) — draft → static analysis → diff → submit for approval → (impact
 * analysis when required) → a SECOND user approves/rejects → sign & publish. Maker-checker is
 * structural: SAME_ACTOR (403) blocks self-approval; GATE_UNSATISFIED / APPROVAL_REQUIRED (409)
 * render as actionable checklists, not raw errors. Track A endpoints land in parallel, so every call
 * degrades gracefully when an endpoint is not yet live.
 */
export function PublishDrawer(props: PublishDrawerProps) {
  const { open, flowId, version, publishedVersion, source, currentUserSub, onClose, onPublished, onRunItNow } = props;
  const { message } = App.useApp();

  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [diff, setDiff] = useState<DiffResult | null>(null);
  const [approval, setApproval] = useState<Approval | null>(null);
  const [sim, setSim] = useState<SimulationView | null>(null);
  const [published, setPublished] = useState<PublishResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<StepError | null>(null);
  const [note, setNote] = useState('');
  const [decisionNote, setDecisionNote] = useState('');

  const run = useCallback(async (action: string, fn: () => Promise<void>) => {
    setBusy(action);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr({ action, ...normalizeApiError(e) });
    } finally {
      setBusy(null);
    }
  }, []);

  // Read on open, not a dependency. Publishing moves this prop, and that must not count as a reopen: it would
  // wipe the result and offer to publish again, with "Run it now" gone.
  const publishedRef = useRef(publishedVersion);
  publishedRef.current = publishedVersion;

  // On open: reset, run the static analysis + diff passes.
  useEffect(() => {
    if (!open) return;
    setValidation(null);
    setDiff(null);
    setApproval(null);
    setSim(null);
    setPublished(null);
    setErr(null);
    setNote('');
    setDecisionNote('');

    void run('validate', async () => {
      setValidation(await validateFlow(flowId, source, 'json'));
    });
    const against = publishedRef.current;
    if (against != null) {
      void run('diff', async () => {
        setDiff(await diffFlow(flowId, against, version));
      });
    }
  }, [open, flowId, source, version, run]);

  const hasErrors = (validation?.errors.length ?? 0) > 0;
  const impactRequired = approval?.requiredEvidence.impactReportRequired ?? false;
  const impactSatisfied = !impactRequired || Boolean(sim && !sim.blocking);
  const isProposer = Boolean(currentUserSub && approval && approval.proposedBy === currentUserSub);

  const doSubmit = () =>
    run('submit', async () => {
      const a = await submitForApproval(flowId, version, note || undefined);
      setApproval(a);
      message.success('Submitted for approval.');
    });

  const doImpact = () =>
    run('impact', async () => {
      let s = await runSimulation({ flowId, versionId: version, championVersion: publishedVersion ?? undefined, mode: 'MODE_1' });
      // MODE_1 seals the report synchronously; if a deployment returns only an id, fetch it.
      if (!s.report && s.id) {
        try {
          s = await getSimulation(s.id);
        } catch {
          /* keep the POST view (still carries changeClass/blocking/reportHash) */
        }
      }
      setSim(s);
      if (s.blocking) message.warning('Impact analysis flags a blocking regression — review before approval.');
    });

  const doDecision = (decision: 'APPROVE' | 'REJECT') =>
    run('decision', async () => {
      const res = await decideApproval(flowId, approval!.approvalId, decision, {
        note: decisionNote || undefined,
        impactReportId: sim?.id,
      });
      setApproval({ ...approval!, state: res.state });
      message.success(decision === 'APPROVE' ? 'Approved.' : 'Rejected — returned to draft.');
    });

  const doPublish = () =>
    run('publish', async () => {
      const res = await publishFlow(flowId, { version, approvalId: approval?.approvalId, note: note || undefined });
      setPublished(res);
      onPublished(res);
      message.success(`Published v${res.ref.version} · ${res.contentHash.slice(0, 18)}…`);
    });

  const errBanner = (action: string) => {
    if (!err || err.action !== action) return null;
    if (err.code === 'SAME_ACTOR') {
      return (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message="Four-eyes: a different reviewer must approve"
          description="You proposed this change; maker-checker is structural — self-approval is impossible. Ask a second authorised reviewer to approve."
        />
      );
    }
    if (err.code === 'GATE_UNSATISFIED' || err.code === 'APPROVAL_REQUIRED') {
      return (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 8 }}
          message={err.code === 'APPROVAL_REQUIRED' ? 'Approval required before publish' : 'Publish gate not satisfied'}
          description={
            <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
              {(err.missing && err.missing.length > 0 ? err.missing : ['Complete the simulation / approval evidence above.']).map(
                (m) => (
                  <li key={m}>{m}</li>
                ),
              )}
            </ul>
          }
        />
      );
    }
    if (isNotAvailable(err)) {
      return (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 8 }}
          message="Not available yet"
          description={`This step's backend endpoint (${action}) is not live yet — Track A lands it in parallel. The rest of the designer is unaffected.`}
        />
      );
    }
    return <Alert type="error" showIcon style={{ marginTop: 8 }} message={err.code} description={err.message} />;
  };

  // ---- step contents -----------------------------------------------------------------------
  const analysisStep = (
    <div>
      {busy === 'validate' && <Text type="secondary">Compiling…</Text>}
      {validation &&
        (hasErrors ? (
          <Alert
            type="error"
            showIcon
            message={`${validation.errors.length} error(s) — cannot publish`}
            description="Fix the compiler errors on the canvas first (the compiler is the gate)."
          />
        ) : (
          <Text style={{ color: 'var(--ol-positive)' }}>
            <CheckCircleFilled /> Compiles · {validation.warnings.length} warning(s)
          </Text>
        ))}
      {errBanner('validate')}
    </div>
  );

  const diffStep = (
    <div>
      {publishedVersion == null ? (
        <Text type="secondary">First publish — no prior version to diff.</Text>
      ) : busy === 'diff' ? (
        <Text type="secondary">Diffing…</Text>
      ) : diff ? (
        <Space wrap>
          <ChangeClassBadge result={diff} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {diff.changes.length} change(s) vs v{publishedVersion}
          </Text>
        </Space>
      ) : null}
      {errBanner('diff')}
    </div>
  );

  const approvalStep = (
    <div>
      {!approval ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.TextArea
            placeholder="Rationale for this change (recorded, shown to the reviewer) — required by policy"
            autoSize={{ minRows: 2, maxRows: 4 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            type="primary"
            loading={busy === 'submit'}
            disabled={hasErrors || !validation}
            onClick={doSubmit}
          >
            Submit for approval
          </Button>
        </Space>
      ) : (
        <Descriptions size="small" column={1} labelStyle={{ width: 130 }}>
          <Descriptions.Item label="State">
            <Tag color={approval.state === 'APPROVED' ? 'success' : approval.state === 'REJECTED' ? 'error' : 'processing'}>
              {approval.state}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Proposed by">{approval.proposedBy}</Descriptions.Item>
          <Descriptions.Item label="Required evidence">
            {approval.requiredEvidence.modes.length > 0
              ? approval.requiredEvidence.modes.join(', ')
              : 'none'}
            {approval.requiredEvidence.impactReportRequired && (
              <Tag color="gold" bordered={false} style={{ marginInlineStart: 6 }}>
                impact report
              </Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      )}
      {errBanner('submit')}
    </div>
  );

  const impactStep = (
    <div>
      {!impactRequired ? (
        <Text type="secondary">No impact report required for this change class.</Text>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button icon={<ExperimentOutlined />} loading={busy === 'impact'} onClick={doImpact} disabled={!approval}>
            Run impact analysis (Mode 1)
          </Button>
          {sim && (
            <>
            <Descriptions size="small" column={1} labelStyle={{ width: 130 }}>
              <Descriptions.Item label="Change class">{sim.changeClass}</Descriptions.Item>
              <Descriptions.Item label="Compiles">{String(sim.compiles)}</Descriptions.Item>
              <Descriptions.Item label="Blocking">
                <Tag color={sim.blocking ? 'error' : 'success'}>{sim.blocking ? 'yes' : 'no'}</Tag>
              </Descriptions.Item>
              {sim.report?.header?.fidelityGrade && (
                <Descriptions.Item label="Fidelity">{sim.report.header.fidelityGrade}</Descriptions.Item>
              )}
              <Descriptions.Item label="Report">
                <span className="ol-mono" style={{ fontSize: 11 }}>
                  {sim.reportHash.slice(0, 20)}…
                </span>
              </Descriptions.Item>
            </Descriptions>
            <FanOutReport fanOuts={sim.report?.staticSection?.fanOuts} />
            </>
          )}
        </Space>
      )}
      {errBanner('impact')}
    </div>
  );

  const decisionStep = (
    <div>
      {!approval ? (
        <Text type="secondary">Submit for approval first.</Text>
      ) : approval.state !== 'PENDING' ? (
        <Tag color={approval.state === 'APPROVED' ? 'success' : 'error'}>{approval.state}</Tag>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {isProposer && (
            <Alert
              type="info"
              showIcon
              message="You proposed this change — a different reviewer must approve (four-eyes)."
            />
          )}
          <Input.TextArea
            placeholder="Reviewer note (optional)"
            autoSize={{ minRows: 1, maxRows: 3 }}
            value={decisionNote}
            onChange={(e) => setDecisionNote(e.target.value)}
          />
          <Space>
            <Button
              type="primary"
              loading={busy === 'decision'}
              disabled={!impactSatisfied}
              onClick={() => doDecision('APPROVE')}
            >
              Approve
            </Button>
            <Button danger loading={busy === 'decision'} onClick={() => doDecision('REJECT')}>
              Request changes
            </Button>
          </Space>
          {impactRequired && !impactSatisfied && (
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              Approval needs a non-blocking impact report (run it above).
            </Text>
          )}
        </Space>
      )}
      {errBanner('decision')}
    </div>
  );

  const publishStep = (
    <div>
      {published ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="success"
            showIcon
            message={`Signed & published as v${published.ref.version}`}
            description={
              <div className="ol-mono" style={{ fontSize: 11 }}>
                {published.contentHash}
                <br />
                {published.activated ? 'activated on the runtime' : 'signed (runtime not enabled)'}
              </div>
            }
          />
          {/* The terminal step is a NEXT ACTION, not a dead end (owner feedback: "published…
              now what?"): start a run of exactly this version, or wire a trigger. */}
          {onRunItNow && (
            <Space wrap>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => onRunItNow(flowId, published.ref.version)}
                disabled={!published.activated}
              >
                Run it now
              </Button>
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                {published.activated
                  ? `Starts a run of v${published.ref.version}. To start it automatically, wire a trigger from the flow list (Triggers).`
                  : 'The runtime is not enabled in this deployment — the version is signed but cannot run here.'}
              </Text>
            </Space>
          )}
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<SafetyCertificateOutlined />}
            loading={busy === 'publish'}
            disabled={hasErrors || (Boolean(approval) && approval!.state !== 'APPROVED')}
            onClick={doPublish}
          >
            Sign &amp; publish v{version}
          </Button>
          <Text type="secondary" style={{ fontSize: 11 }}>
            AAA-critical action — signs the canonical bytes and activates the version.
          </Text>
        </Space>
      )}
      {errBanner('publish')}
    </div>
  );

  const current = published ? 5 : approval?.state === 'APPROVED' ? 4 : approval ? 3 : validation ? 2 : 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={460}
      title={
        <Space>
          <AuditOutlined />
          Publish · v{version}
        </Space>
      }
      styles={{ body: { padding: 16 } }}
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
        Change must be evidenced, not brave. Every publish runs static analysis → diff → maker-checker
        → simulation gate before it signs.
      </Paragraph>
      <Steps
        direction="vertical"
        size="small"
        current={current}
        items={[
          { title: 'Static analysis', description: analysisStep },
          { title: 'Diff vs published', description: diffStep },
          { title: 'Submit for approval', description: approvalStep },
          { title: 'Impact analysis', description: impactStep, icon: <ExperimentOutlined /> },
          { title: 'Approve / reject (second reviewer)', description: decisionStep },
          { title: 'Sign & publish', description: publishStep },
        ]}
      />
    </Drawer>
  );
}
